<template>
  <div ref="wrapper" :class="wrapperClasses" @keydown="onEscape">
    <Popover
      ref="popoverRef"
      v-bind="popoverProps"
      v-model:open="openProxy"
      @open-change="onVisibleChange"
    >
      <slot>
        <span
          :class="openProxy && 'active'"
          class="bitts-overflow-menu__default-toggle"
        >
          <FontAwesomeIcon
            :icon="['fak', 'menu']"
            :style="{ height: '12px', width: '12px', color: 'currentColor' }"
          />
        </span>
      </slot>

      <template #content>
        <div class="bitts-popover__content" @keydown="onEscape">
          <slot name="content" />
        </div>
      </template>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Popover } from 'ant-design-vue';
import type { PopoverProps } from 'ant-design-vue';
import { computed, ref, toRef, useTemplateRef } from 'vue';

import {
  useViewportPlacement,
  withViewportOverflow,
} from '../composables/useViewportPlacement';
import { FOCUSABLE_SELECTOR } from '../shared/focusableSelector';
import { Placement, PlacementType, Trigger, TriggerType } from '../types';

export type Props = {
  align?: PopoverProps['align'];
  overlayClass?: string;
  overlayStyle?: PopoverProps['overlayStyle'];
  autoAdjustOverflow?: boolean;
  placement?: PlacementType;
  trigger?: TriggerType;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  mountToBody?: boolean;
  destroyOnClose?: boolean;
  showMenu?: boolean | null;
  disableTransition?: boolean;
  transitionName?: string;
  autoclose?: number;
};

const props = withDefaults(defineProps<Props>(), {
  align: undefined,
  overlayClass: '',
  overlayStyle: undefined,
  autoAdjustOverflow: true,
  placement: Placement.Bottom,
  trigger: Trigger.Click,
  mouseEnterDelay: 0,
  mouseLeaveDelay: 0.1,
  mountToBody: false,
  destroyOnClose: false,
  showMenu: null,
  disableTransition: false,
  transitionName: 'fade',
  autoclose: 0,
});

const emit = defineEmits<{
  'menu-visible': [payload: { visible: boolean }];
}>();

type AntTooltipExposed = {
  forcePopupAlign?: () => void;
};

const popoverRef = ref<{
  getPopupDomNode(): Node | null;
  $: { subTree?: { component?: { exposed?: AntTooltipExposed | null } } };
} | null>(null);

const openModel = defineModel<boolean | undefined>('open', {
  default: undefined,
});

function getPopupDomNode(): HTMLElement | null {
  const node = popoverRef.value?.getPopupDomNode();
  return node instanceof HTMLElement ? node : null;
}

const { effectivePlacement, getPopupContainer, onShow, onHide } =
  useViewportPlacement(
    toRef(props, 'placement'),
    toRef(props, 'mountToBody'),
    getPopupDomNode,
  );

// Realigns an open popover when its trigger moves without resizing. ant's
// Popover hides forcePopupAlign, so reach the Tooltip it renders.
function forceAlign() {
  popoverRef.value?.$?.subTree?.component?.exposed?.forcePopupAlign?.();
}

const internalOpen = ref(false);
/** Briefly hands ant a closed state, which it keeps once left uncontrolled again. */
const isClosingOnEscape = ref(false);

const openProxy = computed<boolean | undefined>({
  get() {
    if (openModel.value !== undefined) {
      return openModel.value;
    }

    if (props.showMenu !== null) {
      return props.showMenu;
    }

    if (props.autoclose > 0 || isClosingOnEscape.value) {
      return internalOpen.value;
    }

    return undefined;
  },
  set(val) {
    if (openModel.value !== undefined) {
      openModel.value = val;
      return;
    }

    if (props.showMenu !== null) {
      return;
    }

    internalOpen.value = Boolean(val);
  },
});

const wrapperClasses = computed(() => {
  const classes = ['bitts-overflow-menu'];
  if (props.overlayClass) classes.push(props.overlayClass);
  return classes.join(' ');
});

const isVisible = ref(false);
const wrapper = useTemplateRef<HTMLElement>('wrapper');
let focusBeforeOpen: HTMLElement | null = null;

function onVisibleChange(visible: boolean) {
  if (visible && !isVisible.value) {
    const focused = document.activeElement;
    focusBeforeOpen =
      focused instanceof HTMLElement && wrapper.value?.contains(focused)
        ? focused
        : null;
  }
  isVisible.value = visible;
  emit('menu-visible', { visible });
  openProxy.value = visible;
  visible ? onShow() : onHide();

  if (props.autoclose > 0 && visible) {
    setTimeout(() => {
      openProxy.value = false;
      emit('menu-visible', { visible: false });
    }, props.autoclose);
  }
}

/**
 * The element of the trigger that was focused when the popover opened gets
 * focus back. Without one (a Safari click does not focus buttons, so focus may
 * still be elsewhere on the page, or the popover was opened from code), the
 * first focusable element of the trigger does.
 */
function restoreFocus() {
  const target = focusBeforeOpen?.isConnected
    ? focusBeforeOpen
    : wrapper.value?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  target?.focus();
}

/**
 * Esc closes the popover from its trigger or its own content. It is handled on
 * those elements rather than on the document, so the innermost popover (or an
 * open select, which stops Esc itself) closes first and an enclosing popover or
 * modal never sees the key. Focus left inside goes back to where it was when
 * the popover opened.
 * @see https://www.w3.org/WAI/WCAG22/Understanding/content-on-hover-or-focus (2026-10-13)
 */
function onEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented)
    return;
  if (!(openProxy.value ?? isVisible.value)) return;
  event.preventDefault();
  event.stopPropagation();
  const isFocusInPopup = !!getPopupDomNode()?.contains(document.activeElement);
  isClosingOnEscape.value = true;
  onVisibleChange(false);
  setTimeout(() => {
    isClosingOnEscape.value = false;
  });
  if (isFocusInPopup) restoreFocus();
}

const effectiveAlign = computed(() =>
  props.mountToBody ? withViewportOverflow(props.align) : props.align,
);

const popoverProps = computed<Partial<PopoverProps>>(() => ({
  getPopupContainer,
  align: effectiveAlign.value,
  placement: effectivePlacement.value,
  trigger: props.trigger,
  autoAdjustOverflow: props.autoAdjustOverflow,
  destroyTooltipOnHide: props.destroyOnClose,
  transitionName: props.disableTransition ? undefined : props.transitionName,
  mouseEnterDelay: props.mouseEnterDelay,
  mouseLeaveDelay: props.mouseLeaveDelay,
  overlayClassName: wrapperClasses.value,
  overlayStyle: props.overlayStyle,
}));

defineExpose({ forceAlign, restoreFocus });
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-popover__content {
  display: contents;
}

.bitts-overflow-menu {
  &.ant-popover {
    font-family: inherit;
    .ant-popover-inner {
      @apply p-0;
    }
    a:hover {
      @apply text-neutral-text-strong;
    }
  }
  .ant-popover-inner-content {
    padding: 0;
  }
  .ant-popover-arrow {
    display: none;
  }
  .ant-popover-inner {
    @apply border border-neutral-border rounded-lg;
  }
  &.ant-popover-placement-bottom,
  &.ant-popover-placement-bottomLeft,
  &.ant-popover-placement-bottomRight,
  &.ant-popover-placement-left,
  &.ant-popover-placement-leftTop,
  &.ant-popover-placement-leftBottom,
  &.ant-popover-placement-top,
  &.ant-popover-placement-topLeft,
  &.ant-popover-placement-topRight {
    padding: 0;
  }

  .bitts-overflow-menu__default-toggle {
    @apply h-24 w-24 rounded-bts-sm cursor-pointer
    flex items-center text-neutral-text-placeholder justify-center;

    &.active,
    &:hover {
      @apply bg-neutral-background;
    }
  }
}
</style>
